/**
 * Walkthroughs (design/2026-10-20-walkthroughs.md): a small card that drives
 * the real page step by step, from a file under help/walkthroughs/.
 *
 * App-wide, in App.tsx inside HelpProvider, so a walkthrough survives moving
 * between pages. It owns its own tour runner (help/useTourRunner) over the
 * same page tools as the Help pane, with the step's authored `say` in place
 * of the help model: no help route call is made during a walkthrough.
 *
 * - `<any page>?walkthrough=<slug>` shows the walkthrough's first card
 *   (title, summary, Start) and takes the param off the address, before any
 *   step navigates (a navigation replaces the params).
 * - Start runs step 1; Next and Back move as a Help pane tour does: Back
 *   re-shows a step and runs nothing, and no step's action runs twice.
 * - Each move is kept in sessionStorage, so a reload resumes on the same
 *   step (shown, its action not run again); Finish and the close button
 *   clear it. Finished slugs are ticked in localStorage.
 * - The pane's tour, if one runs, ends first: both draw the one outline ring.
 * - A presentation seed a step made ("Start a new chat from here") is tagged
 *   with the walkthrough's slug; whenever the kept step is cleared (Finish,
 *   close, another link) those seeds go too, so the start screen keeps no
 *   chip the person never asked for.
 */
import React, { useEffect, useRef, useState } from 'react'
import useAccount from '../hooks/useAccount'
import useRouter from '../hooks/useRouter'
import useSnackbar from '../hooks/useSnackbar'
import { useOptionalPageToolsContext } from '../pageTools/PageToolsProvider'
import WalkthroughCard from '../components/help/WalkthroughCard'
import type { WalkthroughCardView } from '../components/help/WalkthroughCard'
import { helpReaderFor } from './features'
import type { HelpReader } from './features'
import { useHelp } from './HelpProvider'
import { currentStep } from './tourRuntime'
import type { TourState } from './tourRuntime'
import { useTourRunner } from './useTourRunner'
import { clearWalkthroughSeeds } from '../hooks/usePresentationSeed'
import { WalkthroughContext, walkthroughSlugOf } from './walkthroughContext'
import type { WalkthroughContextValue } from './walkthroughContext'
import { canStartWalkthrough, walkthroughBySlug, walkthroughToRun } from './walkthroughs'
import type { Walkthrough } from './walkthroughs'

/** The walkthrough on screen and its step, kept so a reload resumes. */
export const WALKTHROUGH_RESUME_KEY = 'platinum.walkthrough'
/** The slugs this browser has finished. */
export const WALKTHROUGHS_DONE_KEY = 'platinum.walkthroughs.done'

export { useWalkthrough, walkthroughSlugOf } from './walkthroughContext'

function readResume(): { slug: string; index: number } | null {
  try {
    const saved = JSON.parse(sessionStorage.getItem(WALKTHROUGH_RESUME_KEY) ?? 'null')
    return saved && typeof saved.slug === 'string' && Number.isInteger(saved.index) ? saved : null
  } catch {
    return null
  }
}

function writeResume(slug: string, index: number) {
  try {
    sessionStorage.setItem(WALKTHROUGH_RESUME_KEY, JSON.stringify({ slug, index }))
  } catch {
    // A browser that keeps nothing still runs the walkthrough; a reload starts again.
  }
}

// The walkthrough is over (or replaced): forget its step and any seed it made.
function clearResume() {
  try {
    sessionStorage.removeItem(WALKTHROUGH_RESUME_KEY)
  } catch {
    // Nothing kept, nothing to clear.
  }
  clearWalkthroughSeeds()
}

function readDone(): string[] {
  try {
    const saved = JSON.parse(localStorage.getItem(WALKTHROUGHS_DONE_KEY) ?? '[]')
    return Array.isArray(saved) ? saved.filter((s): s is string => typeof s === 'string') : []
  } catch {
    return []
  }
}

function writeDone(done: string[]) {
  try {
    localStorage.setItem(WALKTHROUGHS_DONE_KEY, JSON.stringify(done))
  } catch {
    // The tick shows for this visit only.
  }
}

const AUDIENCE_WORDS: Record<HelpReader, string> = {
  public: 'everyone',
  realm: 'admins and Managers',
  manager: 'Managers',
}

/** Why this reader cannot start it. */
function refusal(w: Walkthrough): string {
  return `The walkthrough “${w.title}” is for ${AUDIENCE_WORDS[w.audience ?? 'manager']} only.`
}

export function WalkthroughProvider({ children }: React.PropsWithChildren) {
  const account = useAccount()
  const router = useRouter()
  const snackbar = useSnackbar()
  const help = useHelp()
  const voice = useOptionalPageToolsContext()

  const voiceRef = useRef(voice)
  voiceRef.current = voice
  const helpRef = useRef(help)
  helpRef.current = help
  const routerRef = useRef(router)
  routerRef.current = router

  const signedIn = !!account.user
  const reader = helpReaderFor(account.user?.role_permissions)
  const readerRef = useRef(reader)
  readerRef.current = reader

  // The first card before Start, and the last after Finish.
  const [intro, setIntro] = useState<Walkthrough | null>(null)
  const [finished, setFinished] = useState<Walkthrough | null>(null)
  // The step on screen has been shown: its page is up and its action has run.
  const [ready, setReady] = useState(false)
  // What the runner could not do on this step.
  const [problem, setProblem] = useState<string | null>(null)
  const [done, setDone] = useState<string[]>(readDone)

  const runner = useTourRunner({
    tools: () => voiceRef.current?.livePageTools() ?? [],
    navigateToPage: (page) => voiceRef.current?.navigateToPage(page) ?? null,
    routeName: router.name,
    pageKey: voice?.pageKey,
    // The runner asks for the step to be explained once it is on screen: the
    // card already says it, so that call only marks the step ready.
    explain: async () => setReady(true),
    note: (line) => setProblem(line),
    describe: (step) => step.say ?? '',
  })
  const runnerRef = useRef(runner)
  runnerRef.current = runner

  const active = walkthroughBySlug(walkthroughSlugOf(runner.state) ?? '')

  // Every move starts the step afresh: not ready, nothing gone wrong yet.
  const freshStep = () => {
    setReady(false)
    setProblem(null)
  }

  const begin = (w: Walkthrough, from?: { index: number }) => {
    helpRef.current?.endTour()
    setIntro(null)
    setFinished(null)
    freshStep()
    runnerRef.current.start(walkthroughToRun(w), from)
    writeResume(w.slug, runnerRef.current.current().index)
  }

  const handleStart = (slug: string) => {
    const w = walkthroughBySlug(slug)
    if (!w) {
      snackbar.error(`There is no walkthrough called “${slug}”.`)
      return
    }
    if (!canStartWalkthrough(readerRef.current, w)) {
      snackbar.error(refusal(w))
      return
    }
    begin(w)
  }

  const handleNext = () => {
    const now = runner.current()
    if (!active || !now.tour) return
    const last = now.index + 1 >= now.tour.steps.length
    freshStep()
    runner.next()
    if (!last) {
      writeResume(active.slug, runner.current().index)
      return
    }
    clearResume()
    const next = done.includes(active.slug) ? done : [...done, active.slug]
    writeDone(next)
    setDone(next)
    setFinished(active)
  }

  const handleBack = () => {
    if (!active) return
    freshStep()
    runner.back()
    writeResume(active.slug, runner.current().index)
  }

  const handleClose = () => {
    if (runner.current().tour) runner.end()
    clearResume()
    freshStep()
    setIntro(null)
    setFinished(null)
  }

  // A link with ?walkthrough=<slug>: show its first card and take the param
  // off the address now, before a step's navigation would drop it (and so
  // Back does not bring it back). Not signed in, the link waits for the
  // login to bring the param back.
  const param = (router.params?.walkthrough as string | undefined) ?? ''
  useEffect(() => {
    if (!param || !signedIn) return
    routerRef.current.removeParams(['walkthrough'], { history: 'replace' })
    const w = walkthroughBySlug(param)
    if (!w) {
      snackbar.error(`There is no walkthrough called “${param}”.`)
      return
    }
    if (!canStartWalkthrough(readerRef.current, w)) {
      snackbar.error(refusal(w))
      return
    }
    if (runnerRef.current.current().tour) runnerRef.current.end()
    clearResume()
    freshStep()
    setFinished(null)
    setIntro(w)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [param, signedIn])

  // A reload mid-walkthrough: once signed in, resume on the kept step,
  // unless a link names a walkthrough of its own.
  const resumedRef = useRef(false)
  useEffect(() => {
    if (!signedIn || resumedRef.current) return
    resumedRef.current = true
    if (routerRef.current.params?.walkthrough) return
    const saved = readResume()
    if (!saved) return
    const w = walkthroughBySlug(saved.slug)
    if (!w || !canStartWalkthrough(readerRef.current, w)) {
      clearResume()
      return
    }
    begin(w, { index: saved.index })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signedIn])

  const step = currentStep(runner.state)
  const view: WalkthroughCardView | null = active && runner.state.tour && step
    ? {
        kind: 'step',
        walkthrough: active,
        number: runner.state.index + 1,
        total: runner.state.tour.steps.length,
        say: step.say ?? '',
        did: runner.state.did,
        problem: problem ?? undefined,
        status: problem ? 'error' : runner.state.phase === 'navigating' || !ready ? 'navigating' : 'ready',
      }
    : intro
      ? { kind: 'intro', walkthrough: intro }
      : finished
        ? { kind: 'done', walkthrough: finished }
        : null

  const value: WalkthroughContextValue = { start: handleStart, state: runner.state, current: () => runnerRef.current.current(), done }

  return (
    <WalkthroughContext.Provider value={value}>
      {children}
      {signedIn && view && (
        <WalkthroughCard
          view={view}
          onStart={() => intro && begin(intro)}
          onNext={handleNext}
          onBack={handleBack}
          onClose={handleClose}
        />
      )}
    </WalkthroughContext.Provider>
  )
}

export default WalkthroughProvider
